import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Shield, FileText, Users, Clock, CheckCircle, ArrowRight, LogOut, Building2 } from 'lucide-react';
import { AuthContext } from '../App';

const WelcomePage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F7' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              {/* ReqTrak Logo */}
              <div className="flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_foi-manager/artifacts/v2fks7vg_reqtrak-logo.png" 
                  alt="ReqTrak Logo" 
                  className="h-12 w-auto"
                />
                <div className="ml-3">
                  <h1 className="text-2xl font-bold" style={{ color: '#1A2B48' }}>Police Records Portal</h1>
                  <p className="text-sm" style={{ color: '#2E7D6A' }}>Powered by ReqTrak Platform</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <span className="text-slate-600">Welcome, {user.full_name}</span>
                  <Link to="/dashboard">
                    <Button variant="ghost" className="text-slate-600 hover:text-blue-600">
                      Dashboard
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    className="text-slate-600 hover:text-red-600"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-600 hover:text-blue-600">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #F4F4F7 0%, #E8F4F8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: '#1A2B48' }}>
              Modern Police Records Management Platform
            </h1>
            <p className="text-xl mb-8 leading-relaxed" style={{ color: '#5A6169' }}>
              Transform how police departments handle public records requests. 
              Streamline submissions, automate processing, and enhance transparency with our comprehensive SaaS platform.
            </p>
            
            {/* User Type Selection Help */}
            <div className="mb-8 p-6 bg-white/10 rounded-lg backdrop-blur-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1A2B48' }}>👥 For Citizens</h3>
                  <p className="text-sm" style={{ color: '#5A6169' }}>
                    Submit and track public records requests from your local police department.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1A2B48' }}>🏢 For Police Departments</h3>
                  <p className="text-sm" style={{ color: '#5A6169' }}>
                    Register your department to get your own branded portal and manage requests efficiently.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <>
                  <Link to="/request/new">
                    <Button 
                      className="text-white px-8 py-3 text-lg"
                      style={{ 
                        background: 'linear-gradient(135deg, #1A2B48 0%, #2E7D6A 100%)',
                        border: 'none'
                      }}
                    >
                      Submit New Request
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button 
                      variant="outline" 
                      className="px-8 py-3 text-lg"
                      style={{ 
                        borderColor: '#1A2B48',
                        color: '#1A2B48'
                      }}
                    >
                      View Dashboard
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register">
                    <Button 
                      className="text-white px-8 py-3 text-lg"
                      style={{ 
                        background: 'linear-gradient(135deg, #1A2B48 0%, #2E7D6A 100%)',
                        border: 'none'
                      }}
                    >
                      Citizen Registration
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/register-department">
                    <Button 
                      className="text-white px-8 py-3 text-lg"
                      style={{ 
                        background: 'linear-gradient(135deg, #FF568B 0%, #1A2B48 100%)',
                        border: 'none'
                      }}
                    >
                      Register Your Department
                      <Building2 className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#1A2B48' }}>Platform Features</h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: '#5A6169' }}>
              Comprehensive SaaS solution for modern police departments and public records management
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#E8F4F8' }}>
                  <FileText className="w-6 h-6" style={{ color: '#2E7D6A' }} />
                </div>
                <CardTitle className="text-xl" style={{ color: '#1A2B48' }}>Multi-Tenant Platform</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="leading-relaxed" style={{ color: '#5A6169' }}>
                  Each police department gets their own branded portal with complete data isolation and customization.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FFE8F0' }}>
                  <Users className="w-6 h-6" style={{ color: '#FF568B' }} />
                </div>
                <CardTitle className="text-xl" style={{ color: '#1A2B48' }}>AI-Powered Processing</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="leading-relaxed" style={{ color: '#5A6169' }}>
                  Smart request classification, automated responses, and intelligent document analysis to streamline workflows.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#E8F2FF' }}>
                  <Shield className="w-6 h-6" style={{ color: '#1A2B48' }} />
                </div>
                <CardTitle className="text-xl" style={{ color: '#1A2B48' }}>Enterprise Security</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="leading-relaxed" style={{ color: '#5A6169' }}>
                  Advanced security monitoring, role-based access control, and compliance-ready audit trails.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0F1B2E 0%, #1A2B48 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Transform Your Department?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Join the modern era of public records management with our comprehensive platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                className="px-8 py-3 text-lg bg-white text-blue-900 hover:bg-gray-100"
                style={{ color: '#1A2B48' }}
              >
                Citizens: Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/register-department">
              <Button 
                variant="outline" 
                className="px-8 py-3 text-lg border-white text-white hover:bg-white hover:text-blue-900"
              >
                Departments: Register Here
                <Building2 className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <span className="text-xl font-bold text-white">ReqTrak</span>
              </div>
              <p className="text-gray-400">
                Modern police records management platform for the digital age.
              </p>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Platform</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
                <li><a href="#" className="hover:text-white">Status</a></li>
                <li><a href="#" className="hover:text-white">Community</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white">Compliance</a></li>
                <li><a href="#" className="hover:text-white">GDPR</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 ReqTrak Inc. All rights reserved. | Modern police records management platform.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WelcomePage;